import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SubscriptionStatus {
  subscribed: boolean;
  product_id: string | null;
  price_id: string | null;
  subscription_end: string | null;
  loading: boolean;
}

interface PlanInfo {
  id: string;
  name: string;
  price: number;
  product_id: string;
  price_id: string;
}

// Plan mapping with Stripe product/price IDs
export const SUBSCRIPTION_PLANS: Record<string, PlanInfo> = {
  basic: {
    id: "basic",
    name: "Basic Plan",
    price: 29,
    product_id: "prod_T3VskPPSh6LORX",
    price_id: "price_1S7Oqd6aKP7aLbmsxv2r5uaK",
  },
  pro: {
    id: "pro", 
    name: "Pro Plan",
    price: 79,
    product_id: "prod_T3VsE4Lz3QjIbs",
    price_id: "price_1S7Oqp6aKP7aLbms9iaT0ReH",
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise Plan", 
    price: 199,
    product_id: "prod_T3VsSjdXmcJ74K",
    price_id: "price_1S7Oqz6aKP7aLbms56HuFaV4",
  },
};

export function useSubscription() {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    subscribed: false,
    product_id: null,
    price_id: null,
    subscription_end: null,
    loading: true,
  });

  const { toast } = useToast();

  // Check subscription status
  const checkSubscription = useCallback(async () => {
    try {
      setSubscriptionStatus(prev => ({ ...prev, loading: true }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setSubscriptionStatus({
          subscribed: false,
          product_id: null,
          price_id: null,
          subscription_end: null,
          loading: false,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error checking subscription:", error);
        throw error;
      }

      setSubscriptionStatus({
        subscribed: data.subscribed || false,
        product_id: data.product_id || null,
        price_id: data.price_id || null,
        subscription_end: data.subscription_end || null,
        loading: false,
      });

    } catch (error) {
      console.error("Failed to check subscription:", error);
      setSubscriptionStatus(prev => ({ ...prev, loading: false }));
      toast({
        title: "Fehler",
        description: "Subscription-Status konnte nicht abgerufen werden.",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Create checkout session
  const createCheckout = useCallback(async (plan: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Anmeldung erforderlich",
          description: "Bitte melden Sie sich an, um ein Abonnement abzuschließen.",
          variant: "destructive",
        });
        return;
      }

      if (!SUBSCRIPTION_PLANS[plan]) {
        throw new Error(`Invalid plan: ${plan}`);
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error creating checkout:", error);
        throw error;
      }

      if (data.url) {
        // Open checkout in new tab
        window.open(data.url, "_blank");
      }

    } catch (error) {
      console.error("Failed to create checkout:", error);
      toast({
        title: "Fehler",
        description: "Checkout-Session konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Open customer portal
  const openCustomerPortal = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Anmeldung erforderlich",
          description: "Bitte melden Sie sich an.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error opening customer portal:", error);
        throw error;
      }

      if (data.url) {
        // Open portal in new tab
        window.open(data.url, "_blank");
      }

    } catch (error) {
      console.error("Failed to open customer portal:", error);
      toast({
        title: "Fehler",
        description: "Kunden-Portal konnte nicht geöffnet werden.",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Get current plan info
  const getCurrentPlan = useCallback(() => {
    if (!subscriptionStatus.product_id) return null;
    
    return Object.values(SUBSCRIPTION_PLANS).find(
      plan => plan.product_id === subscriptionStatus.product_id
    ) || null;
  }, [subscriptionStatus.product_id]);

  // Auto-refresh subscription status
  useEffect(() => {
    checkSubscription();

    // Check subscription status when auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      checkSubscription();
    });

    // Auto-refresh every 5 minutes
    const interval = setInterval(checkSubscription, 5 * 60 * 1000);

    return () => {
      authListener?.subscription?.unsubscribe();
      clearInterval(interval);
    };
  }, [checkSubscription]);

  return {
    subscriptionStatus,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    getCurrentPlan,
    plans: SUBSCRIPTION_PLANS,
  };
}